import { useEffect, useState, useRef, useCallback } from "react";
import { Room, createLocalAudioTrack, RoomEvent } from "livekit-client";

const API_BASE = "https://api.syncplay.live";

export default function useVoiceRoom(roomId, username) {
    const roomRef = useRef(null);
    const [connected, setConnected] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [isMuted, setIsMuted] = useState(true);
    const audioTrackRef = useRef(null);
    const updateParticipantsRef = useRef(null);
    const audioElementsRef = useRef(new Map());
    const isConnectingRef = useRef(false);
    const isTogglingMuteRef = useRef(false); // ⭐

    const updateParticipants = useCallback(() => {
        const room = roomRef.current;
        if (!room) return;
        const list = [];
        try {
            if (room.localParticipant) {
                list.push({
                    id: room.localParticipant.identity,
                    name: username,
                    isSpeaking: room.localParticipant.isSpeaking || false,
                    isMuted: !room.localParticipant.microphoneEnabled,
                    local: true
                });
            }
            if (room.remoteParticipants?.size > 0) {
                room.remoteParticipants.forEach((p) => {
                    list.push({
                        id: p.identity,
                        name: p.name || p.identity,
                        isSpeaking: p.isSpeaking || false,
                        isMuted: !p.microphoneEnabled,
                        local: false
                    });
                });
            }
            setParticipants([...list]);
        } catch (err) {
        }
    }, [username]);

    updateParticipantsRef.current = updateParticipants;

    const cleanup = useCallback(() => {
        audioElementsRef.current.forEach((el) => {
            if (el?.parentNode) { el.pause(); el.srcObject = null; el.remove(); }
        });
        audioElementsRef.current.clear();
        if (audioTrackRef.current) { audioTrackRef.current.stop(); audioTrackRef.current = null; }
        if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; }
        setConnected(false);
        setParticipants([]);
        setIsMuted(true);
        isConnectingRef.current = false;
    }, []);

    const connect = useCallback(async () => {
        if (!roomId || !username || isConnectingRef.current) return;
        
        if (roomRef.current) {
            cleanup();
            await new Promise(r => setTimeout(r, 500));
        }
        isConnectingRef.current = true;

        try {
            const params = new URLSearchParams({ roomId, username, userId: crypto.randomUUID() });
            const res = await fetch(`${API_BASE}/webrtc/token?${params.toString()}`);
            if (!res.ok) { isConnectingRef.current = false; return; }
            const data = await res.json();
            if (!data.success) { isConnectingRef.current = false; return; }

            const token = data.data.token;
            const room = new Room({ adaptiveStream: false, dynacast: false });
            roomRef.current = room;
            
            

            room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === 'audio' && participant) {
                    const audioElement = document.createElement('audio');
                    audioElement.autoplay = true;
                    audioElement.volume = 0.8;
                    audioElement.style.display = 'none';
                    document.body.appendChild(audioElement);
                    track.attach(audioElement);
                    audioElementsRef.current.set(participant.identity, audioElement);
                }
                setTimeout(updateParticipantsRef.current, 200);
            });

            room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
                if (participant) {
                    const audioEl = audioElementsRef.current.get(participant.identity);
                    if (audioEl) { audioEl.remove(); audioElementsRef.current.delete(participant.identity); }
                }
                setTimeout(updateParticipantsRef.current, 200);
            });

            room.on(RoomEvent.ParticipantConnected, () => setTimeout(updateParticipantsRef.current, 200));
            room.on(RoomEvent.ParticipantDisconnected, (participant) => {
                if (participant?.identity) {
                    const audioEl = audioElementsRef.current.get(participant.identity);
                    if (audioEl) { audioEl.remove(); audioElementsRef.current.delete(participant.identity); }
                }
                setTimeout(updateParticipantsRef.current, 200);
            });

            room.on(RoomEvent.ActiveSpeakersChanged, () => updateParticipantsRef.current?.());
            room.on(RoomEvent.Connected, () => { setConnected(true); setTimeout(updateParticipantsRef.current, 500); });
            room.on(RoomEvent.Disconnected, () => {
                setConnected(false); setParticipants([]);
                audioElementsRef.current.forEach((el) => el.remove());
                audioElementsRef.current.clear();
            });

            await room.connect("wss://api.syncplay.live/livekit", token);

            setTimeout(updateParticipantsRef.current, 1000);
            isConnectingRef.current = false;

        } catch (err) {
            setConnected(false);
            isConnectingRef.current = false;
        }
    }, [roomId, username, cleanup]);

    const disconnect = useCallback(() => {
        cleanup();
    }, [cleanup]);

    // Auto-connect on mount
    useEffect(() => {
        if (roomId && username) {
            connect();
        }
        return () => { cleanup(); };
    }, [roomId, username]);

    const toggleMute = async () => {
    if (!roomRef.current?.localParticipant) return;
    if (isTogglingMuteRef.current) return;

    isTogglingMuteRef.current = true;
    const newMuted = !isMuted;

    try {
        if (!newMuted) {
            if (!audioTrackRef.current) {
                const audioTrack = await createLocalAudioTrack({ 
                    echoCancellation: true, 
                    noiseSuppression: true, 
                    autoGainControl: true 
                });
                audioTrackRef.current = audioTrack;
                await roomRef.current.localParticipant.publishTrack(audioTrack);
            }
            await roomRef.current.localParticipant.setMicrophoneEnabled(true);
        } else {
            if (audioTrackRef.current) {
                await roomRef.current.localParticipant.setMicrophoneEnabled(false);
            }
        }
        setIsMuted(newMuted);
    } catch (err) {
        setIsMuted(newMuted);
    } finally {
        setTimeout(() => { isTogglingMuteRef.current = false; }, 500);
    }

    setTimeout(updateParticipantsRef.current, 100);
};

    return { connected, participants, isMuted, toggleMute, disconnect, connect };
}